/**
 * Shared plumbing for the staff-gated admin API (`ixadmin_api`) and the
 * integrations API.
 *
 * Every service module that calls those APIs repeats the same four steps: read
 * the base URL out of the Vite env, join it with a request path, forward the
 * caller's Supabase bearer token, and turn a non-2xx response into a readable
 * reason. The private copies only ever differed by which error class they threw,
 * so the helpers here take an error factory and each module keeps its own error
 * type.
 *
 * `onboarding.ts`, `knowledgePricing.ts` and `generation.ts` keep their own
 * session and error-response handling on purpose: they map the same failures to
 * their own client-facing copy (error codes, "Your session has expired") rather
 * than the staff-facing messages here. `knowledgePricing.ts` and `generation.ts`
 * still share the URL helpers.
 */

import { supabase } from '@/lib/supabase';

/**
 * Builds the error a caller wants thrown. Each service passes its own class, so
 * `instanceof GeoPublishError` still holds for everything that service throws.
 */
export type ServiceErrorFactory = (message: string, status?: number | null) => Error;

/** Reads the admin API base URL, throwing the caller's error when it is unset. */
export function getAdminApiBaseUrl(fail: ServiceErrorFactory): string {
    const baseUrl = import.meta.env.VITE_ADMIN_API_BASE_URL?.trim();
    if (!baseUrl) {
        throw fail('VITE_ADMIN_API_BASE_URL is not configured');
    }
    return baseUrl;
}

/** Joins an API base URL with a request path, whatever slashes either carries. */
export function buildApiUrl(baseUrl: string, path: string): URL {
    const normalized = baseUrl.endsWith('/') ? baseUrl : `${baseUrl}/`;
    return new URL(path.replace(/^\//, ''), normalized);
}

/** Returns the signed-in user's access token for an `Authorization` header. */
export async function getSupabaseAccessToken(fail: ServiceErrorFactory): Promise<string> {
    const {
        data: { session },
        error,
    } = await supabase.auth.getSession();
    if (error) {
        throw fail(`Failed to get Supabase session: ${error.message}`);
    }
    const accessToken = session?.access_token;
    if (!accessToken) {
        throw fail('Missing Supabase access token. Please sign in again.', 401);
    }
    return accessToken;
}

/**
 * Pulls a readable reason out of a failed response: FastAPI's `detail` when it
 * is a string, its JSON when it is a validation-error object, otherwise the raw
 * body. Returns null when there is nothing useful to show, so callers fall back
 * to their own status-code message.
 */
export async function parseErrorDetail(response: Response): Promise<string | null> {
    const contentType = response.headers.get('content-type') || '';
    if (contentType.includes('application/json')) {
        try {
            const payload = (await response.json()) as { detail?: unknown };
            if (typeof payload.detail === 'string') {
                return payload.detail.trim() ? payload.detail : null;
            }
            if (payload.detail && typeof payload.detail === 'object') {
                return JSON.stringify(payload.detail);
            }
            return null;
        } catch {
            return null;
        }
    }
    try {
        const text = await response.text();
        return text.trim() || null;
    } catch {
        return null;
    }
}
